import React from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    Divider,
    ListItemIcon,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemButton,
    ListItemText
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useFarms } from 'modules/farm/api/queries';
import { PATH_DASHBOARD } from 'routes/paths';
import FarmDialog from 'modules/farm/components/FarmDialog';
import { usePermitToActionFeature } from 'modules/user/api/selector';
import { CRUDAction, FeatureKey } from 'common/constants/permission';
import type { SelectChangeEvent } from '@mui/material/Select';

interface MenuFarmProps {
    open: boolean;
}

const MenuFarm: React.FC<MenuFarmProps> = ({ open }) => {
    const { t } = useTranslation('layout');
    const { farmId } = useParams();
    const navigate = useNavigate();
    const showAddFarm = usePermitToActionFeature([CRUDAction.Create], FeatureKey.Farm);
    const [openNewFarmDialog, setOpenNewFarmDialog] = React.useState(false);
    const [farmAnchorEl, setFarmAnchorEl] = React.useState<null | HTMLElement>(null);
    const { data, isLoading } = useFarms();

    const [selectedFarm, setSelectedFarm] = React.useState<string>(farmId || 'all');

    const handleSelectFarm = (eventOrValue: SelectChangeEvent<string> | string) => {
        let id: string;

        if (typeof eventOrValue === 'string') {
            id = eventOrValue;
        } else {
            id = eventOrValue.target.value;
        }

        if (id === '__add_farm__') {
            setOpenNewFarmDialog(true);
            return;
        }

        if (id === 'all') {
            setSelectedFarm('all');
            navigate(PATH_DASHBOARD.farms.root);
            return;
        }
        navigate(PATH_DASHBOARD.farms.detail(id));
    };

    const handleFarmIconClick = (event: React.MouseEvent<HTMLElement>) => {
        setFarmAnchorEl(event.currentTarget);
    };

    const handleFarmPopoverClose = () => setFarmAnchorEl(null);

    React.useEffect(() => {
        if (farmId) setSelectedFarm(farmId);
        else setSelectedFarm('all');
    }, [farmId]);

    return (
        <Box mb={2} mx={2}>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                mb={1}
                sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }}
            >
                {t('allFarms')}
            </Typography>

            {open ? (
                <FormControl fullWidth size="small">
                    <Select
                        disabled={isLoading}
                        value={selectedFarm}
                        displayEmpty
                        onChange={handleSelectFarm}
                        startAdornment={
                            <Box sx={{ mr: 2 }} display="flex" alignItems="center">
                                <img src="/static/icons/layout/farm.svg" width={44} height={44} />
                            </Box>
                        }
                        sx={{ borderRadius: 1 }}
                    >
                        <MenuItem value="all" onClick={() => handleSelectFarm('all')}>
                            {t('allFarms')}
                        </MenuItem>

                        {data?.farms.map((farm: any) => (
                            <MenuItem
                                value={farm.id}
                                key={farm.id}
                                onClick={() => handleSelectFarm(farm.id)}
                            >
                                {farm.name}
                            </MenuItem>
                        ))}

                        {showAddFarm &&
                            [
                                <Divider key="select-divider" />,
                                <MenuItem
                                    key="select-add-farm"
                                    value="__add_farm__"
                                    sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Icon icon="mdi:plus-box-outline" width={20} height={20} />
                                    </ListItemIcon>
                                    {t('addFarm')}
                                </MenuItem>
                            ]}
                    </Select>
                </FormControl>
            ) : (
                <>
                    <IconButton
                        disabled={isLoading}
                        onClick={handleFarmIconClick}
                        sx={{
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <img src="/static/icons/layout/farm.svg" width={25} height={25} />
                    </IconButton>

                    <Popover
                        open={Boolean(farmAnchorEl)}
                        anchorEl={farmAnchorEl}
                        onClose={handleFarmPopoverClose}
                        anchorOrigin={{
                            vertical: 'center',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'center',
                            horizontal: 'left'
                        }}
                        PaperProps={{ sx: { ml: 3, p: 0, minWidth: 200 } }}
                    >
                        <Box>
                            <List disablePadding>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={selectedFarm === 'all'}
                                        onClick={() => {
                                            handleSelectFarm('all');
                                            handleFarmPopoverClose();
                                        }}
                                        sx={{ px: 2, py: 1.2 }}
                                    >
                                        <ListItemText primary={t('allFarms')} />
                                    </ListItemButton>
                                </ListItem>

                                {data?.farms.map((farm: any) => (
                                    <ListItem disablePadding key={farm.id}>
                                        <ListItemButton
                                            selected={selectedFarm === farm.id}
                                            onClick={() => {
                                                handleSelectFarm(farm.id);
                                                handleFarmPopoverClose();
                                            }}
                                            sx={{
                                                px: 2,
                                                py: 1.2,
                                                '&.Mui-selected': { bgcolor: 'action.selected' }
                                            }}
                                        >
                                            <ListItemText primary={farm.name} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}

                                {showAddFarm &&
                                    [
                                        <Divider key="popover-divider" />,
                                        <ListItem
                                            key="popover-add-farm"
                                            button
                                            onClick={() => {
                                                handleSelectFarm('__add_farm__');
                                                handleFarmPopoverClose();
                                            }}
                                            sx={{ px: 2, py: 1.2, color: 'primary.main' }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <Icon icon="mdi:plus-box-outline" width={20} height={20} />
                                            </ListItemIcon>
                                            <ListItemText primary={t('addFarm')} />
                                        </ListItem>
                                    ]}
                            </List>
                        </Box>
                    </Popover>
                </>
            )}

            <FarmDialog open={openNewFarmDialog} onClose={() => setOpenNewFarmDialog(false)} />
        </Box>
    );
};

export default MenuFarm;
